import { useCallback, useMemo } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { differenceInCalendarDays } from "date-fns";
import { toastManager } from "@salestrak/ui";
import {
  ClearAllTrashDocument,
  connectionRows,
  DeleteTrashItemPermanentlyDocument,
  GetTrashDocument,
  RestoreIssueDocument,
  RestoreTrashItemDocument,
  type TrashRow,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

const trackTrash = (
  event: (typeof SettingsEvent)[keyof typeof SettingsEvent],
  data?: Record<string, unknown>,
) => trackEvent({ context: AnalyticsContext.Settings, event, label: "trash", ...(data ? { data } : {}) });

/** Trash retention window enforced by core — rows auto-delete after this. */
export const TRASH_RETENTION_DAYS = 30;

/** One query loads the whole trash: the server hard-caps the listing at 1000
 *  rows (core's TRASH_CAP), so "everything" is bounded and the flat table can
 *  interleave server and local rows by deletion time without offset
 *  bookkeeping. */
const TRASH_QUERY_LIMIT = 1000;

/** The workspace's trashed issues, search-narrowed server-side. The restore /
 *  delete mutations evict the `trash` field, so the list refetches itself. */
export function useTrashList(search: string): {
  issues: TrashRow[];
  total: number;
  initialLoading: boolean;
} {
  const variables = useMemo(
    () => ({
      limit: TRASH_QUERY_LIMIT,
      where: search ? { search } : undefined,
    }),
    [search],
  );
  const { data, loading } = useQuery(GetTrashDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });
  const issues = useMemo(() => connectionRows(data?.trash), [data?.trash]);
  return {
    issues,
    total: data?.trash?.total ?? 0,
    initialLoading: loading && !data,
  };
}

/** The moment a trashed row auto-deletes: `trashedAt` plus the retention
 *  window, as an ISO string. Null when the row carries no deletion time. */
export function trashPurgeAt(trashedAt: string | null | undefined): string | null {
  if (!trashedAt) return null;
  const purge = new Date(trashedAt);
  purge.setDate(purge.getDate() + TRASH_RETENTION_DAYS);
  return purge.toISOString();
}

/**
 * Days left before a trashed row auto-deletes. Derived from `Issue.trashedAt`,
 * so it is computed at render time and never carried on the row. Counted in
 * calendar days from today to the purge date, so the count tracks the
 * "Deleted" date beside it (two rows deleted on one day always read alike);
 * whole 24-hour periods made the count depend on the time of day.
 */
export function trashDaysRemaining(
  trashedAt: string | null | undefined,
  now: Date = new Date(),
): number {
  const purgeAt = trashPurgeAt(trashedAt);
  if (!purgeAt) return TRASH_RETENTION_DAYS;
  return Math.max(0, differenceInCalendarDays(new Date(purgeAt), now));
}

// A restore/delete result union member that is not the entity is a typed
// failure — the mutation resolved, so no `catch` fires and only this branch
// can stop the success toast.
function typedFailure(
  result: { __typename?: string; message?: string } | null | undefined,
  fallback: string,
): boolean {
  if (result && result.__typename === "Issue") return false;
  toastManager.add({
    title: result?.message || fallback,
    type: "error",
  });
  return true;
}

/** The restore toast. Restore clears only the trash: a Project archived
 *  before it was deleted comes back archived, out of lists and search, so
 *  the toast says where it went rather than leaving a restore that looks
 *  like it failed. */
export function restoredToast(
  result: { archived?: boolean | null } | null | undefined,
): { title: string; description?: string } {
  return result?.archived
    ? {
        title: "Project restored to Archived",
        description: "Unarchive it to see it in lists and search.",
      }
    : { title: "Project restored" };
}

/** The one toast for a bulk restore (the bulk-delete Undo). Archived rows
 *  are named for the same reason as `restoredToast`. */
export function bulkRestoredToast(
  restored: number,
  archived: number,
): { title: string; description?: string } {
  const noun = `${restored.toLocaleString()} Project${restored !== 1 ? "s" : ""}`;
  if (!archived) return { title: `${noun} restored` };
  return {
    title: `${noun} restored`,
    description: `${archived.toLocaleString()} ${archived === 1 ? "is" : "are"} still archived. Unarchive ${archived === 1 ? "it" : "them"} to see ${archived === 1 ? "it" : "them"} in lists and search.`,
  };
}

export function useTrashMutations() {
  const [restoreMutation] = useMutation(RestoreTrashItemDocument);
  const { cache } = useApolloClient();
  const [restoreIssueMutation] = useMutation(RestoreIssueDocument);
  const [deleteMutation] = useMutation(DeleteTrashItemPermanentlyDocument);
  const [clearMutation] = useMutation(ClearAllTrashDocument);

  // The trash.manage path — restores anything in the workspace's trash.
  const restoreItem = useCallback(
    async (itemId: string, itemType: string = "Issue") => {
      try {
        const { data } = await restoreMutation({
          variables: { itemId, itemType },
          update(cache) {
            cache.evict({ fieldName: "trash" });
            cache.gc();
          },
        });
        const restored = data?.restoreTrashItem;
        if (typedFailure(restored, "Failed to restore item")) return;
        trackTrash(SettingsEvent.Restore, { itemType });
        toastManager.add({
          ...restoredToast(restored?.__typename === "Issue" ? restored : null),
          type: "success",
        });
      } catch (err) {
        toastMutationError(err, "Failed to restore item");
      }
    },
    [restoreMutation],
  );

  // The self-serve path — undoing one's own delete. Rides `restoreIssue`
  // (delete.items + per-item access), so it works for everyone the delete
  // itself worked for; `restoreTrashItem` above needs trash.manage.
  const restoreIssue = useCallback(
    async (issueId: string): Promise<boolean> => {
      try {
        const { data } = await restoreIssueMutation({
          variables: { issueId },
          update(cache) {
            cache.evict({ fieldName: "trash" });
            cache.gc();
          },
        });
        const restored = data?.restoreIssue;
        if (typedFailure(restored, "Failed to restore item")) return false;
        toastManager.add({
          ...restoredToast(restored?.__typename === "Issue" ? restored : null),
          type: "success",
        });
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to restore item");
        return false;
      }
    },
    [restoreIssueMutation],
  );

  // The bulk self-serve path — the bulk-delete Undo. One restoreIssue per id
  // (there is no bulk restore), settled together, and ONE toast for the batch
  // instead of a toast per Project.
  const restoreIssues = useCallback(
    async (issueIds: string[]): Promise<void> => {
      const results = await Promise.allSettled(
        issueIds.map((issueId) => restoreIssueMutation({ variables: { issueId } })),
      );
      cache.evict({ fieldName: "trash" });
      cache.gc();
      let restored = 0;
      let archived = 0;
      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const issue = result.value.data?.restoreIssue;
        if (issue?.__typename !== "Issue") continue;
        restored += 1;
        if (issue.archived) archived += 1;
      }
      if (restored) {
        toastManager.add({ ...bulkRestoredToast(restored, archived), type: "success" });
      }
      const failed = issueIds.length - restored;
      if (failed) {
        toastManager.add({
          title: `${failed.toLocaleString()} Project${failed !== 1 ? "s" : ""} could not be restored`,
          type: "error",
        });
      }
    },
    [cache, restoreIssueMutation],
  );

  const deleteItemPermanently = useCallback(
    async (itemId: string, itemType: string = "Issue") => {
      try {
        const { data } = await deleteMutation({
          variables: { itemId, itemType },
          update(cache, { data }) {
            const result = data?.deleteTrashItemPermanently;
            cache.evict({ fieldName: "trash" });
            if (result?.__typename === "DeleteTrashItemSuccess") {
              cache.evict({ id: `Issue:${result.itemId}` });
            }
            cache.gc();
          },
        });
        const result = data?.deleteTrashItemPermanently;
        if (result?.__typename !== "DeleteTrashItemSuccess") {
          toastManager.add({
            title:
              (result && "message" in result && result.message) ||
              "Failed to delete item",
            type: "error",
          });
          return;
        }
        trackTrash(SettingsEvent.DeletePermanently, { itemType });
      } catch (err) {
        toastMutationError(err, "Failed to delete item");
      }
    },
    [deleteMutation],
  );

  const clearAllTrash = useCallback(
    async (where?: { search?: string }) => {
      try {
        const { data } = await clearMutation({
          variables: { where },
          update(cache) {
            cache.evict({ fieldName: "trash" });
            cache.gc();
          },
        });
        const result = data?.clearAllTrash;
        if (result?.__typename === "ClearAllTrashSuccess") {
          trackTrash(SettingsEvent.ClearAll, {
            count: result.deletedCount as number,
            searched: !!where?.search,
          });
          return result.deletedCount as number;
        }
        toastManager.add({
          title:
            (result && "message" in result && result.message) ||
            "Failed to clear trash",
          type: "error",
        });
        return 0;
      } catch (err) {
        toastMutationError(err, "Failed to clear trash");
        return 0;
      }
    },
    [clearMutation],
  );

  return { restoreItem, restoreIssue, restoreIssues, deleteItemPermanently, clearAllTrash };
}
